/**
 * Country Agency Slug Translations — Maps translated URL slugs to country keys
 * Mirrors country-slug-translations.ts but for "software development agency + country" pages
 * Non-Latin scripts (ar, zh, ja, ko, ru) keep English slugs to avoid URL encoding issues.
 */

export type AgencyCountryKey =
  | 'morocco' | 'france' | 'germany' | 'uk' | 'usa' | 'saudi-arabia' | 'uae'
  | 'spain' | 'canada' | 'netherlands' | 'belgium' | 'switzerland' | 'italy'
  | 'qatar' | 'portugal' | 'japan' | 'korea' | 'turkey' | 'brazil' | 'india'
  | 'australia' | 'south-africa' | 'mexico' | 'colombia' | 'poland'
  | 'sweden' | 'denmark' | 'philippines' | 'nigeria' | 'egypt' | 'vietnam'
  | 'greece' | 'kenya' | 'argentina' | 'ukraine' | 'russia' | 'china'
  | 'thailand' | 'malaysia' | 'scandinavia'
  | 'peru' | 'chile' | 'ghana' | 'senegal' | 'south_africa' | 'indonesia' | 'singapore'
  | 'romania' | 'bulgaria' | 'czech_republic' | 'hungary';

/**
 * For each language, maps translated slug → agency country key
 */
export const TRANSLATED_AGENCY_SLUGS: Record<string, Record<string, AgencyCountryKey>> = {
  // ─── FRENCH ────────────────────────────────────────────
  fr: {
    'agence-developpement-informatique-maroc': 'morocco',
    'agence-developpement-informatique-france': 'france',
    'agence-developpement-informatique-allemagne': 'germany',
    'agence-developpement-informatique-royaume-uni': 'uk',
    'agence-developpement-informatique-etats-unis': 'usa',
    'agence-developpement-informatique-arabie-saoudite': 'saudi-arabia',
    'agence-developpement-informatique-emirats': 'uae',
    'agence-developpement-informatique-espagne': 'spain',
    'agence-developpement-informatique-canada': 'canada',
    'agence-developpement-informatique-pays-bas': 'netherlands',
    'agence-developpement-informatique-belgique': 'belgium',
    'agence-developpement-informatique-suisse': 'switzerland',
    'agence-developpement-informatique-italie': 'italy',
    'agence-developpement-informatique-qatar': 'qatar',
    'agence-developpement-informatique-portugal': 'portugal',
    'agence-developpement-informatique-japon': 'japan',
    'agence-developpement-informatique-coree': 'korea',
    'agence-developpement-informatique-turquie': 'turkey',
    'agence-developpement-informatique-bresil': 'brazil',
    'agence-developpement-informatique-inde': 'india',
    'agence-developpement-informatique-australie': 'australia',
    'agence-developpement-informatique-afrique-du-sud': 'south-africa',
    'agence-developpement-informatique-mexique': 'mexico',
    'agence-developpement-informatique-colombie': 'colombia',
    'agence-developpement-informatique-pologne': 'poland',
    'agence-developpement-informatique-suede': 'sweden',
    'agence-developpement-informatique-danemark': 'denmark',
    'agence-developpement-informatique-philippines': 'philippines',
    'agence-developpement-informatique-nigeria': 'nigeria',
    'agence-developpement-informatique-egypte': 'egypt',
    'agence-developpement-informatique-vietnam': 'vietnam',
    'agence-developpement-informatique-grece': 'greece',
    'agence-developpement-informatique-kenya': 'kenya',
    'agence-developpement-informatique-argentine': 'argentina',
    'agence-developpement-informatique-ukraine': 'ukraine',
    'agence-developpement-informatique-russie': 'russia',
    'agence-developpement-informatique-chine': 'china',
    'agence-developpement-informatique-thailande': 'thailand',
    'agence-developpement-informatique-malaisie': 'malaysia',
    'agence-developpement-informatique-scandinavie': 'scandinavia',
    'agence-developpement-informatique-perou': 'peru',
    'agence-developpement-informatique-chili': 'chile',
    'agence-developpement-informatique-ghana': 'ghana',
    'agence-developpement-informatique-senegal': 'senegal',
    'agence-developpement-informatique-afrique-sud': 'south_africa',
    'agence-developpement-informatique-indonesie': 'indonesia',
    'agence-developpement-informatique-singapour': 'singapore',
    'agence-developpement-informatique-roumanie': 'romania',
    'agence-developpement-informatique-bulgarie': 'bulgaria',
    'agence-developpement-informatique-republique-tcheque': 'czech_republic',
    'agence-developpement-informatique-hongrie': 'hungary',
  },

  es: {
    'agencia-desarrollo-software-marruecos': 'morocco',
    'agencia-desarrollo-software-francia': 'france',
    'agencia-desarrollo-software-alemania': 'germany',
    'agencia-desarrollo-software-reino-unido': 'uk',
    'agencia-desarrollo-software-estados-unidos': 'usa',
    'agencia-desarrollo-software-arabia-saudita': 'saudi-arabia',
    'agencia-desarrollo-software-emiratos': 'uae',
    'agencia-desarrollo-software-espana': 'spain',
    'agencia-desarrollo-software-canada': 'canada',
    'agencia-desarrollo-software-paises-bajos': 'netherlands',
    'agencia-desarrollo-software-belgica': 'belgium',
    'agencia-desarrollo-software-suiza': 'switzerland',
    'agencia-desarrollo-software-italia': 'italy',
    'agencia-desarrollo-software-qatar': 'qatar',
    'agencia-desarrollo-software-portugal': 'portugal',
    'agencia-desarrollo-software-japon': 'japan',
    'agencia-desarrollo-software-corea': 'korea',
    'agencia-desarrollo-software-turquia': 'turkey',
    'agencia-desarrollo-software-brasil': 'brazil',
    'agencia-desarrollo-software-india': 'india',
    'agencia-desarrollo-software-australia': 'australia',
    'agencia-desarrollo-software-sudafrica': 'south-africa',
    'agencia-desarrollo-software-mexico': 'mexico',
    'agencia-desarrollo-software-colombia': 'colombia',
    'agencia-desarrollo-software-polonia': 'poland',
    'agencia-desarrollo-software-suecia': 'sweden',
    'agencia-desarrollo-software-dinamarca': 'denmark',
    'agencia-desarrollo-software-filipinas': 'philippines',
    'agencia-desarrollo-software-nigeria': 'nigeria',
    'agencia-desarrollo-software-egipto': 'egypt',
    'agencia-desarrollo-software-vietnam': 'vietnam',
    'agencia-desarrollo-software-grecia': 'greece',
    'agencia-desarrollo-software-kenia': 'kenya',
    'agencia-desarrollo-software-argentina': 'argentina',
    'agencia-desarrollo-software-ucrania': 'ukraine',
    'agencia-desarrollo-software-rusia': 'russia',
    'agencia-desarrollo-software-china': 'china',
    'agencia-desarrollo-software-tailandia': 'thailand',
    'agencia-desarrollo-software-malasia': 'malaysia',
    'agencia-desarrollo-software-escandinavia': 'scandinavia',
    'agencia-desarrollo-software-peru': 'peru',
    'agencia-desarrollo-software-chile': 'chile',
    'agencia-desarrollo-software-ghana': 'ghana',
    'agencia-desarrollo-software-senegal': 'senegal',
    'agencia-desarrollo-software-sudafrica-2': 'south_africa',
    'agencia-desarrollo-software-indonesia': 'indonesia',
    'agencia-desarrollo-software-singapur': 'singapore',
    'agencia-desarrollo-software-rumania': 'romania',
    'agencia-desarrollo-software-bulgaria': 'bulgaria',
    'agencia-desarrollo-software-republica-checa': 'czech_republic',
    'agencia-desarrollo-software-hungria': 'hungary',
  },

  de: {
    'softwareentwicklung-agentur-marokko': 'morocco',
    'softwareentwicklung-agentur-frankreich': 'france',
    'softwareentwicklung-agentur-deutschland': 'germany',
    'softwareentwicklung-agentur-grossbritannien': 'uk',
    'softwareentwicklung-agentur-usa': 'usa',
    'softwareentwicklung-agentur-saudi-arabien': 'saudi-arabia',
    'softwareentwicklung-agentur-vae': 'uae',
    'softwareentwicklung-agentur-spanien': 'spain',
    'softwareentwicklung-agentur-kanada': 'canada',
    'softwareentwicklung-agentur-niederlande': 'netherlands',
    'softwareentwicklung-agentur-belgien': 'belgium',
    'softwareentwicklung-agentur-schweiz': 'switzerland',
    'softwareentwicklung-agentur-italien': 'italy',
    'softwareentwicklung-agentur-katar': 'qatar',
    'softwareentwicklung-agentur-portugal': 'portugal',
    'softwareentwicklung-agentur-japan': 'japan',
    'softwareentwicklung-agentur-suedkorea': 'korea',
    'softwareentwicklung-agentur-tuerkei': 'turkey',
    'softwareentwicklung-agentur-brasilien': 'brazil',
    'softwareentwicklung-agentur-indien': 'india',
    'softwareentwicklung-agentur-australien': 'australia',
    'softwareentwicklung-agentur-suedafrika': 'south-africa',
    'softwareentwicklung-agentur-mexiko': 'mexico',
    'softwareentwicklung-agentur-kolumbien': 'colombia',
    'softwareentwicklung-agentur-polen': 'poland',
    'softwareentwicklung-agentur-schweden': 'sweden',
    'softwareentwicklung-agentur-daenemark': 'denmark',
    'softwareentwicklung-agentur-philippinen': 'philippines',
    'softwareentwicklung-agentur-nigeria': 'nigeria',
    'softwareentwicklung-agentur-aegypten': 'egypt',
    'softwareentwicklung-agentur-vietnam': 'vietnam',
    'softwareentwicklung-agentur-griechenland': 'greece',
    'softwareentwicklung-agentur-kenia': 'kenya',
    'softwareentwicklung-agentur-argentinien': 'argentina',
    'softwareentwicklung-agentur-ukraine': 'ukraine',
    'softwareentwicklung-agentur-russland': 'russia',
    'softwareentwicklung-agentur-china': 'china',
    'softwareentwicklung-agentur-thailand': 'thailand',
    'softwareentwicklung-agentur-malaysia': 'malaysia',
    'softwareentwicklung-agentur-skandinavien': 'scandinavia',
    'softwareentwicklung-agentur-peru': 'peru',
    'softwareentwicklung-agentur-chile': 'chile',
    'softwareentwicklung-agentur-ghana': 'ghana',
    'softwareentwicklung-agentur-senegal': 'senegal',
    'softwareentwicklung-agentur-suedafrika-2': 'south_africa',
    'softwareentwicklung-agentur-indonesien': 'indonesia',
    'softwareentwicklung-agentur-singapur': 'singapore',
    'softwareentwicklung-agentur-rumaenien': 'romania',
    'softwareentwicklung-agentur-bulgarien': 'bulgaria',
    'softwareentwicklung-agentur-tschechien': 'czech_republic',
    'softwareentwicklung-agentur-ungarn': 'hungary',
  },

  it: {
    'agenzia-sviluppo-software-marocco': 'morocco',
    'agenzia-sviluppo-software-francia': 'france',
    'agenzia-sviluppo-software-germania': 'germany',
    'agenzia-sviluppo-software-regno-unito': 'uk',
    'agenzia-sviluppo-software-stati-uniti': 'usa',
    'agenzia-sviluppo-software-arabia-saudita': 'saudi-arabia',
    'agenzia-sviluppo-software-emirati': 'uae',
    'agenzia-sviluppo-software-spagna': 'spain',
    'agenzia-sviluppo-software-canada': 'canada',
    'agenzia-sviluppo-software-paesi-bassi': 'netherlands',
    'agenzia-sviluppo-software-belgio': 'belgium',
    'agenzia-sviluppo-software-svizzera': 'switzerland',
    'agenzia-sviluppo-software-italia': 'italy',
    'agenzia-sviluppo-software-qatar': 'qatar',
    'agenzia-sviluppo-software-portogallo': 'portugal',
    'agenzia-sviluppo-software-giappone': 'japan',
    'agenzia-sviluppo-software-corea': 'korea',
    'agenzia-sviluppo-software-turchia': 'turkey',
    'agenzia-sviluppo-software-brasile': 'brazil',
    'agenzia-sviluppo-software-india': 'india',
    'agenzia-sviluppo-software-australia': 'australia',
    'agenzia-sviluppo-software-sudafrica': 'south-africa',
    'agenzia-sviluppo-software-messico': 'mexico',
    'agenzia-sviluppo-software-colombia': 'colombia',
    'agenzia-sviluppo-software-polonia': 'poland',
    'agenzia-sviluppo-software-svezia': 'sweden',
    'agenzia-sviluppo-software-danimarca': 'denmark',
    'agenzia-sviluppo-software-filippine': 'philippines',
    'agenzia-sviluppo-software-nigeria': 'nigeria',
    'agenzia-sviluppo-software-egitto': 'egypt',
    'agenzia-sviluppo-software-vietnam': 'vietnam',
    'agenzia-sviluppo-software-grecia': 'greece',
    'agenzia-sviluppo-software-kenya': 'kenya',
    'agenzia-sviluppo-software-argentina': 'argentina',
    'agenzia-sviluppo-software-ucraina': 'ukraine',
    'agenzia-sviluppo-software-russia': 'russia',
    'agenzia-sviluppo-software-cina': 'china',
    'agenzia-sviluppo-software-thailandia': 'thailand',
    'agenzia-sviluppo-software-malesia': 'malaysia',
    'agenzia-sviluppo-software-scandinavia': 'scandinavia',
    'agenzia-sviluppo-software-peru': 'peru',
    'agenzia-sviluppo-software-cile': 'chile',
    'agenzia-sviluppo-software-ghana': 'ghana',
    'agenzia-sviluppo-software-senegal': 'senegal',
    'agenzia-sviluppo-software-sudafrica-2': 'south_africa',
    'agenzia-sviluppo-software-indonesia': 'indonesia',
    'agenzia-sviluppo-software-singapore': 'singapore',
    'agenzia-sviluppo-software-romania': 'romania',
    'agenzia-sviluppo-software-bulgaria': 'bulgaria',
    'agenzia-sviluppo-software-repubblica-ceca': 'czech_republic',
    'agenzia-sviluppo-software-ungheria': 'hungary',
  },

  pt: {
    'agencia-desenvolvimento-software-marrocos': 'morocco',
    'agencia-desenvolvimento-software-franca': 'france',
    'agencia-desenvolvimento-software-alemanha': 'germany',
    'agencia-desenvolvimento-software-reino-unido': 'uk',
    'agencia-desenvolvimento-software-estados-unidos': 'usa',
    'agencia-desenvolvimento-software-arabia-saudita': 'saudi-arabia',
    'agencia-desenvolvimento-software-emirados': 'uae',
    'agencia-desenvolvimento-software-espanha': 'spain',
    'agencia-desenvolvimento-software-canada': 'canada',
    'agencia-desenvolvimento-software-paises-baixos': 'netherlands',
    'agencia-desenvolvimento-software-belgica': 'belgium',
    'agencia-desenvolvimento-software-suica': 'switzerland',
    'agencia-desenvolvimento-software-italia': 'italy',
    'agencia-desenvolvimento-software-qatar': 'qatar',
    'agencia-desenvolvimento-software-portugal': 'portugal',
    'agencia-desenvolvimento-software-japao': 'japan',
    'agencia-desenvolvimento-software-coreia': 'korea',
    'agencia-desenvolvimento-software-turquia': 'turkey',
    'agencia-desenvolvimento-software-brasil': 'brazil',
    'agencia-desenvolvimento-software-india': 'india',
    'agencia-desenvolvimento-software-australia': 'australia',
    'agencia-desenvolvimento-software-africa-do-sul': 'south-africa',
    'agencia-desenvolvimento-software-mexico': 'mexico',
    'agencia-desenvolvimento-software-colombia': 'colombia',
    'agencia-desenvolvimento-software-polonia': 'poland',
    'agencia-desenvolvimento-software-suecia': 'sweden',
    'agencia-desenvolvimento-software-dinamarca': 'denmark',
    'agencia-desenvolvimento-software-filipinas': 'philippines',
    'agencia-desenvolvimento-software-nigeria': 'nigeria',
    'agencia-desenvolvimento-software-egito': 'egypt',
    'agencia-desenvolvimento-software-vietna': 'vietnam',
    'agencia-desenvolvimento-software-grecia': 'greece',
    'agencia-desenvolvimento-software-quenia': 'kenya',
    'agencia-desenvolvimento-software-argentina': 'argentina',
    'agencia-desenvolvimento-software-ucrania': 'ukraine',
    'agencia-desenvolvimento-software-russia': 'russia',
    'agencia-desenvolvimento-software-china': 'china',
    'agencia-desenvolvimento-software-tailandia': 'thailand',
    'agencia-desenvolvimento-software-malasia': 'malaysia',
    'agencia-desenvolvimento-software-escandinavia': 'scandinavia',
    'agencia-desenvolvimento-software-peru': 'peru',
    'agencia-desenvolvimento-software-chile': 'chile',
    'agencia-desenvolvimento-software-gana': 'ghana',
    'agencia-desenvolvimento-software-senegal': 'senegal',
    'agencia-desenvolvimento-software-africa-do-sul-2': 'south_africa',
    'agencia-desenvolvimento-software-indonesia': 'indonesia',
    'agencia-desenvolvimento-software-singapura': 'singapore',
    'agencia-desenvolvimento-software-romenia': 'romania',
    'agencia-desenvolvimento-software-bulgaria': 'bulgaria',
    'agencia-desenvolvimento-software-republica-checa': 'czech_republic',
    'agencia-desenvolvimento-software-hungria': 'hungary',
  },

  nl: {
    'software-ontwikkeling-bureau-marokko': 'morocco',
    'software-ontwikkeling-bureau-frankrijk': 'france',
    'software-ontwikkeling-bureau-duitsland': 'germany',
    'software-ontwikkeling-bureau-verenigd-koninkrijk': 'uk',
    'software-ontwikkeling-bureau-amerika': 'usa',
    'software-ontwikkeling-bureau-saoedi-arabie': 'saudi-arabia',
    'software-ontwikkeling-bureau-vae': 'uae',
    'software-ontwikkeling-bureau-spanje': 'spain',
    'software-ontwikkeling-bureau-canada': 'canada',
    'software-ontwikkeling-bureau-nederland': 'netherlands',
    'software-ontwikkeling-bureau-belgie': 'belgium',
    'software-ontwikkeling-bureau-zwitserland': 'switzerland',
    'software-ontwikkeling-bureau-italie': 'italy',
    'software-ontwikkeling-bureau-qatar': 'qatar',
    'software-ontwikkeling-bureau-portugal': 'portugal',
    'software-ontwikkeling-bureau-japan': 'japan',
    'software-ontwikkeling-bureau-zuid-korea': 'korea',
    'software-ontwikkeling-bureau-turkije': 'turkey',
    'software-ontwikkeling-bureau-brazilie': 'brazil',
    'software-ontwikkeling-bureau-india': 'india',
    'software-ontwikkeling-bureau-australie': 'australia',
    'software-ontwikkeling-bureau-zuid-afrika': 'south-africa',
    'software-ontwikkeling-bureau-mexico': 'mexico',
    'software-ontwikkeling-bureau-colombia': 'colombia',
    'software-ontwikkeling-bureau-polen': 'poland',
    'software-ontwikkeling-bureau-zweden': 'sweden',
    'software-ontwikkeling-bureau-denemarken': 'denmark',
    'software-ontwikkeling-bureau-filipijnen': 'philippines',
    'software-ontwikkeling-bureau-nigeria': 'nigeria',
    'software-ontwikkeling-bureau-egypte': 'egypt',
    'software-ontwikkeling-bureau-vietnam': 'vietnam',
    'software-ontwikkeling-bureau-griekenland': 'greece',
    'software-ontwikkeling-bureau-kenia': 'kenya',
    'software-ontwikkeling-bureau-argentinie': 'argentina',
    'software-ontwikkeling-bureau-oekraine': 'ukraine',
    'software-ontwikkeling-bureau-rusland': 'russia',
    'software-ontwikkeling-bureau-china': 'china',
    'software-ontwikkeling-bureau-thailand': 'thailand',
    'software-ontwikkeling-bureau-maleisie': 'malaysia',
    'software-ontwikkeling-bureau-scandinavie': 'scandinavia',
    'software-ontwikkeling-bureau-peru': 'peru',
    'software-ontwikkeling-bureau-chili': 'chile',
    'software-ontwikkeling-bureau-ghana': 'ghana',
    'software-ontwikkeling-bureau-senegal': 'senegal',
    'software-ontwikkeling-bureau-zuid-afrika-2': 'south_africa',
    'software-ontwikkeling-bureau-indonesie': 'indonesia',
    'software-ontwikkeling-bureau-singapore': 'singapore',
    'software-ontwikkeling-bureau-roemenie': 'romania',
    'software-ontwikkeling-bureau-bulgarije': 'bulgaria',
    'software-ontwikkeling-bureau-tsjechie': 'czech_republic',
    'software-ontwikkeling-bureau-hongarije': 'hungary',
  },

  pl: {
    'agencja-rozwoju-oprogramowania-maroko': 'morocco',
    'agencja-rozwoju-oprogramowania-francja': 'france',
    'agencja-rozwoju-oprogramowania-niemcy': 'germany',
    'agencja-rozwoju-oprogramowania-wielka-brytania': 'uk',
    'agencja-rozwoju-oprogramowania-stany-zjednoczone': 'usa',
    'agencja-rozwoju-oprogramowania-arabia-saudyjska': 'saudi-arabia',
    'agencja-rozwoju-oprogramowania-zea': 'uae',
    'agencja-rozwoju-oprogramowania-hiszpania': 'spain',
    'agencja-rozwoju-oprogramowania-kanada': 'canada',
    'agencja-rozwoju-oprogramowania-holandia': 'netherlands',
    'agencja-rozwoju-oprogramowania-belgia': 'belgium',
    'agencja-rozwoju-oprogramowania-szwajcaria': 'switzerland',
    'agencja-rozwoju-oprogramowania-wlochy': 'italy',
    'agencja-rozwoju-oprogramowania-katar': 'qatar',
    'agencja-rozwoju-oprogramowania-portugalia': 'portugal',
    'agencja-rozwoju-oprogramowania-japonia': 'japan',
    'agencja-rozwoju-oprogramowania-korea': 'korea',
    'agencja-rozwoju-oprogramowania-turcja': 'turkey',
    'agencja-rozwoju-oprogramowania-brazylia': 'brazil',
    'agencja-rozwoju-oprogramowania-indie': 'india',
    'agencja-rozwoju-oprogramowania-australia': 'australia',
    'agencja-rozwoju-oprogramowania-rpa': 'south-africa',
    'agencja-rozwoju-oprogramowania-meksyk': 'mexico',
    'agencja-rozwoju-oprogramowania-kolumbia': 'colombia',
    'agencja-rozwoju-oprogramowania-polska': 'poland',
    'agencja-rozwoju-oprogramowania-szwecja': 'sweden',
    'agencja-rozwoju-oprogramowania-dania': 'denmark',
    'agencja-rozwoju-oprogramowania-filipiny': 'philippines',
    'agencja-rozwoju-oprogramowania-nigeria': 'nigeria',
    'agencja-rozwoju-oprogramowania-egipt': 'egypt',
    'agencja-rozwoju-oprogramowania-wietnam': 'vietnam',
    'agencja-rozwoju-oprogramowania-grecja': 'greece',
    'agencja-rozwoju-oprogramowania-kenia': 'kenya',
    'agencja-rozwoju-oprogramowania-argentyna': 'argentina',
    'agencja-rozwoju-oprogramowania-ukraina': 'ukraine',
    'agencja-rozwoju-oprogramowania-rosja': 'russia',
    'agencja-rozwoju-oprogramowania-chiny': 'china',
    'agencja-rozwoju-oprogramowania-tajlandia': 'thailand',
    'agencja-rozwoju-oprogramowania-malezja': 'malaysia',
    'agencja-rozwoju-oprogramowania-skandynawia': 'scandinavia',
    'agencja-rozwoju-oprogramowania-peru': 'peru',
    'agencja-rozwoju-oprogramowania-chile': 'chile',
    'agencja-rozwoju-oprogramowania-ghana': 'ghana',
    'agencja-rozwoju-oprogramowania-senegal': 'senegal',
    'agencja-rozwoju-oprogramowania-rpa-2': 'south_africa',
    'agencja-rozwoju-oprogramowania-indonezja': 'indonesia',
    'agencja-rozwoju-oprogramowania-singapur': 'singapore',
    'agencja-rozwoju-oprogramowania-rumunia': 'romania',
    'agencja-rozwoju-oprogramowania-bulgaria': 'bulgaria',
    'agencja-rozwoju-oprogramowania-czechy': 'czech_republic',
    'agencja-rozwoju-oprogramowania-wegry': 'hungary',
  },

  tr: {
    'yazilim-gelistirme-ajansi-fas': 'morocco',
    'yazilim-gelistirme-ajansi-fransa': 'france',
    'yazilim-gelistirme-ajansi-almanya': 'germany',
    'yazilim-gelistirme-ajansi-ingiltere': 'uk',
    'yazilim-gelistirme-ajansi-abd': 'usa',
    'yazilim-gelistirme-ajansi-suudi-arabistan': 'saudi-arabia',
    'yazilim-gelistirme-ajansi-bae': 'uae',
    'yazilim-gelistirme-ajansi-ispanya': 'spain',
    'yazilim-gelistirme-ajansi-kanada': 'canada',
    'yazilim-gelistirme-ajansi-hollanda': 'netherlands',
    'yazilim-gelistirme-ajansi-belcika': 'belgium',
    'yazilim-gelistirme-ajansi-isvicre': 'switzerland',
    'yazilim-gelistirme-ajansi-italya': 'italy',
    'yazilim-gelistirme-ajansi-katar': 'qatar',
    'yazilim-gelistirme-ajansi-portekiz': 'portugal',
    'yazilim-gelistirme-ajansi-japonya': 'japan',
    'yazilim-gelistirme-ajansi-guney-kore': 'korea',
    'yazilim-gelistirme-ajansi-turkiye': 'turkey',
    'yazilim-gelistirme-ajansi-brezilya': 'brazil',
    'yazilim-gelistirme-ajansi-hindistan': 'india',
    'yazilim-gelistirme-ajansi-avustralya': 'australia',
    'yazilim-gelistirme-ajansi-guney-afrika': 'south-africa',
    'yazilim-gelistirme-ajansi-meksika': 'mexico',
    'yazilim-gelistirme-ajansi-kolombiya': 'colombia',
    'yazilim-gelistirme-ajansi-polonya': 'poland',
    'yazilim-gelistirme-ajansi-isvec': 'sweden',
    'yazilim-gelistirme-ajansi-danimarka': 'denmark',
    'yazilim-gelistirme-ajansi-filipinler': 'philippines',
    'yazilim-gelistirme-ajansi-nijerya': 'nigeria',
    'yazilim-gelistirme-ajansi-misir': 'egypt',
    'yazilim-gelistirme-ajansi-vietnam': 'vietnam',
    'yazilim-gelistirme-ajansi-yunanistan': 'greece',
    'yazilim-gelistirme-ajansi-kenya': 'kenya',
    'yazilim-gelistirme-ajansi-arjantin': 'argentina',
    'yazilim-gelistirme-ajansi-ukrayna': 'ukraine',
    'yazilim-gelistirme-ajansi-rusya': 'russia',
    'yazilim-gelistirme-ajansi-cin': 'china',
    'yazilim-gelistirme-ajansi-tayland': 'thailand',
    'yazilim-gelistirme-ajansi-malezya': 'malaysia',
    'yazilim-gelistirme-ajansi-iskandinavya': 'scandinavia',
    'yazilim-gelistirme-ajansi-peru': 'peru',
    'yazilim-gelistirme-ajansi-sili': 'chile',
    'yazilim-gelistirme-ajansi-gana': 'ghana',
    'yazilim-gelistirme-ajansi-senegal': 'senegal',
    'yazilim-gelistirme-ajansi-guney-afrika-2': 'south_africa',
    'yazilim-gelistirme-ajansi-endonezya': 'indonesia',
    'yazilim-gelistirme-ajansi-singapur': 'singapore',
    'yazilim-gelistirme-ajansi-romanya': 'romania',
    'yazilim-gelistirme-ajansi-bulgaristan': 'bulgaria',
    'yazilim-gelistirme-ajansi-cek-cumhuriyeti': 'czech_republic',
    'yazilim-gelistirme-ajansi-macaristan': 'hungary',
  },

  sv: {
    'programvaruutveckling-byra-marocko': 'morocco',
    'programvaruutveckling-byra-frankrike': 'france',
    'programvaruutveckling-byra-tyskland': 'germany',
    'programvaruutveckling-byra-storbritannien': 'uk',
    'programvaruutveckling-byra-usa': 'usa',
    'programvaruutveckling-byra-saudiarabien': 'saudi-arabia',
    'programvaruutveckling-byra-foerenade-arabemiraten': 'uae',
    'programvaruutveckling-byra-spanien': 'spain',
    'programvaruutveckling-byra-kanada': 'canada',
    'programvaruutveckling-byra-nederlanderna': 'netherlands',
    'programvaruutveckling-byra-belgien': 'belgium',
    'programvaruutveckling-byra-schweiz': 'switzerland',
    'programvaruutveckling-byra-italien': 'italy',
    'programvaruutveckling-byra-qatar': 'qatar',
    'programvaruutveckling-byra-portugal': 'portugal',
    'programvaruutveckling-byra-japan': 'japan',
    'programvaruutveckling-byra-sydkorea': 'korea',
    'programvaruutveckling-byra-turkiet': 'turkey',
    'programvaruutveckling-byra-brasilien': 'brazil',
    'programvaruutveckling-byra-indien': 'india',
    'programvaruutveckling-byra-australien': 'australia',
    'programvaruutveckling-byra-sydafrika': 'south-africa',
    'programvaruutveckling-byra-mexiko': 'mexico',
    'programvaruutveckling-byra-colombia': 'colombia',
    'programvaruutveckling-byra-polen': 'poland',
    'programvaruutveckling-byra-sverige': 'sweden',
    'programvaruutveckling-byra-danmark': 'denmark',
    'programvaruutveckling-byra-filippinerna': 'philippines',
    'programvaruutveckling-byra-nigeria': 'nigeria',
    'programvaruutveckling-byra-egypten': 'egypt',
    'programvaruutveckling-byra-vietnam': 'vietnam',
    'programvaruutveckling-byra-grekland': 'greece',
    'programvaruutveckling-byra-kenya': 'kenya',
    'programvaruutveckling-byra-argentina': 'argentina',
    'programvaruutveckling-byra-ukraina': 'ukraine',
    'programvaruutveckling-byra-ryssland': 'russia',
    'programvaruutveckling-byra-kina': 'china',
    'programvaruutveckling-byra-thailand': 'thailand',
    'programvaruutveckling-byra-malaysia': 'malaysia',
    'programvaruutveckling-byra-skandinavien': 'scandinavia',
    'programvaruutveckling-byra-peru': 'peru',
    'programvaruutveckling-byra-chile': 'chile',
    'programvaruutveckling-byra-ghana': 'ghana',
    'programvaruutveckling-byra-senegal': 'senegal',
    'programvaruutveckling-byra-sydafrika-2': 'south_africa',
    'programvaruutveckling-byra-indonesien': 'indonesia',
    'programvaruutveckling-byra-singapore': 'singapore',
    'programvaruutveckling-byra-rumanien': 'romania',
    'programvaruutveckling-byra-bulgarien': 'bulgaria',
    'programvaruutveckling-byra-tjeckien': 'czech_republic',
    'programvaruutveckling-byra-ungern': 'hungary',
  },

  da: {
    'softwareudvikling-bureau-marokko': 'morocco',
    'softwareudvikling-bureau-frankrig': 'france',
    'softwareudvikling-bureau-tyskland': 'germany',
    'softwareudvikling-bureau-storbritannien': 'uk',
    'softwareudvikling-bureau-usa': 'usa',
    'softwareudvikling-bureau-saudi-arabien': 'saudi-arabia',
    'softwareudvikling-bureau-forenede-arabiske-emirater': 'uae',
    'softwareudvikling-bureau-spanien': 'spain',
    'softwareudvikling-bureau-canada': 'canada',
    'softwareudvikling-bureau-holland': 'netherlands',
    'softwareudvikling-bureau-belgien': 'belgium',
    'softwareudvikling-bureau-schweiz': 'switzerland',
    'softwareudvikling-bureau-italien': 'italy',
    'softwareudvikling-bureau-qatar': 'qatar',
    'softwareudvikling-bureau-portugal': 'portugal',
    'softwareudvikling-bureau-japan': 'japan',
    'softwareudvikling-bureau-sydkorea': 'korea',
    'softwareudvikling-bureau-tyrkiet': 'turkey',
    'softwareudvikling-bureau-brasilien': 'brazil',
    'softwareudvikling-bureau-indien': 'india',
    'softwareudvikling-bureau-australien': 'australia',
    'softwareudvikling-bureau-sydafrika': 'south-africa',
    'softwareudvikling-bureau-mexico': 'mexico',
    'softwareudvikling-bureau-colombia': 'colombia',
    'softwareudvikling-bureau-polen': 'poland',
    'softwareudvikling-bureau-sverige': 'sweden',
    'softwareudvikling-bureau-danmark': 'denmark',
    'softwareudvikling-bureau-filippinerne': 'philippines',
    'softwareudvikling-bureau-nigeria': 'nigeria',
    'softwareudvikling-bureau-egypten': 'egypt',
    'softwareudvikling-bureau-vietnam': 'vietnam',
    'softwareudvikling-bureau-graekenland': 'greece',
    'softwareudvikling-bureau-kenya': 'kenya',
    'softwareudvikling-bureau-argentina': 'argentina',
    'softwareudvikling-bureau-ukraine': 'ukraine',
    'softwareudvikling-bureau-rusland': 'russia',
    'softwareudvikling-bureau-kina': 'china',
    'softwareudvikling-bureau-thailand': 'thailand',
    'softwareudvikling-bureau-malaysia': 'malaysia',
    'softwareudvikling-bureau-skandinavien': 'scandinavia',
    'softwareudvikling-bureau-peru': 'peru',
    'softwareudvikling-bureau-chile': 'chile',
    'softwareudvikling-bureau-ghana': 'ghana',
    'softwareudvikling-bureau-senegal': 'senegal',
    'softwareudvikling-bureau-sydafrika-2': 'south_africa',
    'softwareudvikling-bureau-indonesien': 'indonesia',
    'softwareudvikling-bureau-singapore': 'singapore',
    'softwareudvikling-bureau-rumaenien': 'romania',
    'softwareudvikling-bureau-bulgarien': 'bulgaria',
    'softwareudvikling-bureau-tjekkiet': 'czech_republic',
    'softwareudvikling-bureau-ungarn': 'hungary',
  },
};

/**
 * Get translated agency slug for a country key in a given language
 */
export function getTranslatedAgencySlug(countryKey: AgencyCountryKey, lang: string): string | null {
  const langSlugs = TRANSLATED_AGENCY_SLUGS[lang];
  if (!langSlugs) return null;
  const entry = Object.entries(langSlugs).find(([, key]) => key === countryKey);
  return entry ? entry[0] : null;
}

/**
 * Resolve a slug to its agency country key
 */
export function resolveAgencyCountrySlug(slug: string, lang: string): AgencyCountryKey | null {
  // Check translated slugs first
  const langSlugs = TRANSLATED_AGENCY_SLUGS[lang];
  if (langSlugs && langSlugs[slug]) return langSlugs[slug];

  // Check all languages
  for (const langMap of Object.values(TRANSLATED_AGENCY_SLUGS)) {
    if (langMap[slug]) return langMap[slug];
  }

  // Fallback: extract from English pattern "software-development-agency-for-XXX"
  const match = slug.match(/^software-development-agency-for-(.+)$/);
  if (match) {
    const countryPart = match[1] as AgencyCountryKey;
    return countryPart;
  }

  return null;
}

